import { ReactNode } from 'react'
import { Section } from '@/components/section'
import { HeadlineFont } from '@/setup/font'
import { cn } from '@/utils/styles/cn'

interface PageHeroProps {
    title: ReactNode
    description?: ReactNode
    cta?: ReactNode
    /** Optional visual below the CTA, spanning the page container */
    image?: ReactNode
    /** Extends the inner container of the underlying Section */
    className?: string
    /** Extends the outer full-width layer of the underlying Section, e.g. to hide the hero per breakpoint */
    bleedClassName?: string
}

/**
 * Standard page hero: Kaio display headline with an optional description, CTA and image,
 * centered inside the page container.
 */
export function PageHero({
    title,
    description,
    cta,
    image,
    className,
    bleedClassName,
}: PageHeroProps) {
    return (
        <Section
            bleedClassName={bleedClassName}
            className={cn('flex flex-col items-center py-xl md:py-3xl', className)}
        >
            <h1
                className={cn(
                    HeadlineFont.className,
                    'text-[40px] md:text-[56px] lg:text-[68px] xl:text-[80px] leading-[0.9] uppercase text-center text-balance max-w-[960px]'
                )}
            >
                {title}
            </h1>

            {description && (
                <div className="mt-l text-fg-secondary max-w-[800px] text-center text-p-sm md:text-p-md">
                    {description}
                </div>
            )}

            {cta && <div className="mt-xl w-full flex justify-center">{cta}</div>}

            {image && <div className="mt-xl w-full overflow-hidden rounded-2xl">{image}</div>}
        </Section>
    )
}
